
import "./Form.css";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik"; import * as Yup from "yup";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useCountry } from "../utility/utility";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const validationSchema = Yup.object({
  name: Yup.string()
    .matches(/^[A-Za-z ]+$/, "Only letters and spaces allowed")
    .required("Name is required"),

  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),

  contactNumber: Yup.string()
    .required("Mobile number is required")
    .test("is-valid-phone", "Invalid phone number", (value) =>
      value ? isValidPhoneNumber(value) : false
    ),

  textareaValue: Yup.string().required("Message is required"),
});


export default function Form({ onBookDemo }) {

  const { countryCode } = useCountry();

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}api/v1/info/saveContactInfo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      MySwal.fire({
        title: <p>🎉 Form Submitted!</p>,
        html: "<b>Thank you for booking your demo.</b> We'll get back to you shortly.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#4CAF50",
        background: "#ffffff",
        timer: 3500,
        timerProgressBar: true,
      });

      resetForm();
    } catch (error) {
      console.error(error);
      MySwal.fire({
        title: <p>⚠️ Submission Failed</p>,
        html: "<b>Oops! Something went wrong.</b> Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
        confirmButtonColor: "#f44336",
        background: "#fff0f0",
      });
    }

    setSubmitting(false);
  };

  return (
    <div className="mainclass" id="form">
      <div className="demo-page maxwidth">
        <div className="left-section">
          <p className="badge">Try It Live — Free Demo</p>
          <h1 className="headline heading">
            Get Started with BusinessBay
            <br />
            in 10 minutes.
          </h1>
          <p className="subtext">
            One quick call lets you see BusinessBay in action and understand how it can grow your business.
          </p>

          <div className="features">
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <p className="feature-text">
                <strong>Get started in under 24 hours</strong> — our team sets everything up for you, so you can hit the ground running.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎁</div>
              <p className="feature-text">
                <strong>Free NFC card included</strong> — book this month and claim your digital business card (limited spots available).
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💬</div>
              <p className="feature-text">
                <strong>24/7 customer support</strong> — we’re always ready to help you, answer questions, and keep your business running smoothly.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📍</div>
              <p className="feature-text">
                <strong>Trusted by businesses across UAE & India</strong> —
                local expertise, global results, and solutions that work for your market.
              </p>
            </div>
          </div>
        </div>

        <div className="form-card">
          <h2 className="form-title">Book Your Free Demo</h2>
          <p className="form-subtitle">
            Takes 2 minutes to fill. We'll confirm shortly.
          </p>

          <Formik
            initialValues={{
              name: "",
              email: "",
              contactNumber: "",
              textareaValue: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, setFieldTouched, isSubmitting }) => (

              <FormikForm className="form-container">
                <div className="form-group">
                  <label>Name</label>

                  <Field
                    name="name"
                    type="text"
                    placeholder="Your full name"
                    className="form-input"
                  />
                  <ErrorMessage name="name" component="div" className="error" />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <Field
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    className="form-input"
                  />
                  <ErrorMessage name="email" component="div" className="error" />
                </div>

                <div className="form-group">
                  <label>Mobile Number</label>
                  <PhoneInput
                    defaultCountry={(countryCode || "IN").toUpperCase()}
                    value={values.contactNumber}
                    className="reactPhone"
                    placeholder="Your mobile number"
                    onChange={(value) => setFieldValue("contactNumber", value)}
                    onBlur={() => setFieldTouched("contactNumber", true)}
                  />
                  <ErrorMessage name="contactNumber" component="div" className="error" />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <Field
                    name="textareaValue"
                    as="textarea"
                    rows="4"
                    placeholder="Your message"
                    className="form-input"
                    style={{ height: "60px" }}
                  />
                  <ErrorMessage name="textareaValue" component="div" className="error" />
                </div>

                <div className="form-actions">
                  <button className="btn-primary" disabled={isSubmitting} type="submit">
                    {isSubmitting ? "Submitting..." : "🚀 Book My Free Demo Now"}
                  </button>
                  <p className="divider-text">or connect instantly via</p>
                  <button
                    type="button"
                    className="btn-whatsapp"
                    onClick={() =>
                      window.open(`https://wa.me/${countryCode === "ae" ? "971562198924" : "917835065469"}`, "_blank")
                    }
                  >
                    <WhatsAppIcon />
                    Chat on WhatsApp
                  </button>
                </div>
              </FormikForm>
            )}
          </Formik>

          <p className="security-note">
            🔒 No spam. 100% secure. We'll never share your data.
          </p>
        </div>
      </div>
    </div>
  );
}
